<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <div class="columns">
          <div class="column has-text-centered">
            <h2 class="title is-2">
              Creditors
              <tooltip
                v-if="creditorsTooltipText !== ''"
                :content="creditorsTooltipText"
                :tooltip-options="{
                  classes: ['large'],
                }"
              />
            </h2>
            <div class="subtitle is-2">
              <div v-html="creditorsText ? creditorsText[0].description : ''" />
            </div>
            <checkbox-adv
              id="nothing-to-report"
              v-model="nothingToReport"
              :value="true"
            >
              Check this box if you have no <strong>creditors</strong> to report.
            </checkbox-adv>
          </div>
        </div>
        <div
          v-if="!nothingToReport"
          class="section"
        >
          <validation-observer
            ref="form-creditors"
            tag="form"
          >
            <div class="columns is-multiline">
              <div class="column is-4">
                <textbox-vee
                  id="name"
                  v-model="newItem.name"
                  placeholder="Creditor Name"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-4">
                <textbox-vee
                  id="interest-rate"
                  v-model="newItem.interestRate"
                  type="number"
                  placeholder="Interest Rate (xx.xx%)"
                  rules="percentage|required"
                  required
                />
              </div>
              <div class="column is-4">
                <dropdown-vee
                  v-model="newItem.loanAmount"
                  :options="loanAmounts"
                  placeholder="Loan Amount"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-4">
                <textbox
                  id="street-address"
                  v-model="newItem.streetAddress"
                  placeholder="Street Address"
                />
              </div>
              <div class="column is-3">
                <textbox
                  id="city"
                  v-model="newItem.city"
                  placeholder="City"
                />
              </div>
              <div class="column is-3">
                <dropdown
                  v-model="newItem.state"
                  :options="states"
                  placeholder="State"
                />
              </div>
              <div class="column is-2">
                <textbox-vee
                  id="zip"
                  v-model="newItem.zip"
                  placeholder="Zip Code"
                  rules="zipCode"
                />
              </div>
              <div class="column is-12 has-text-centered">
                <button
                  id="action-btn"
                  type="button"
                  class="button is-secondary"
                  @click="trigerNewItemAction"
                >
                  {{ tableAction }} Creditor
                </button>
              </div>
            </div>
          </validation-observer>
          <div
            v-if="items"
            class="columns"
          >
            <div class="column">
              <v-client-table
                ref="vc-table"
                :data="allItems"
                :columns="table"
                :options="tableOptions"
              >
                <div
                  slot="edit"
                  slot-scope="props"
                >
                  <button
                    :id="`edit-btn-${props.row.id}`"
                    type="button"
                    title="Edit"
                    aria-label="Edit item"
                    class="button is-small is-warning"
                    @click.prevent="editItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-pencil-alt"
                    />
                  </button>
                </div>
                <div
                  slot="delete"
                  slot-scope="props"
                >
                  <button
                    :id="`delete-btn-${props.row.id}`"
                    type="button"
                    title="Delete"
                    aria-label="Delete item"
                    class="button is-small is-danger"
                    @click.prevent="deleteItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-trash"
                    />
                  </button>
                </div>
                <div
                  slot="clone"
                  slot-scope="props"
                >
                  <button
                    :id="`clone-btn-${props.row.id}`"
                    type="button"
                    title="Clone"
                    aria-label="Clone item"
                    class="button is-small is-secondary"
                    @click.prevent="cloneItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-copy"
                    />
                  </button>
                </div>
                <div
                  slot="interestRate"
                  slot-scope="props"
                >
                  {{ props.row.interestRate }}%
                </div>
              </v-client-table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column has-text-centered">
        <button
          id="save-btn"
          type="button"
          class="button is-primary"
          :class="isSaving ? 'is-loading' : ''"
          :disabled="isSaving"
          @click="saveAndContinueCheck('Creditors')"
        >
          Save and Continue
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { ClientTable } from 'vue-tables-2';
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { controlValueHelpers } from '@/mixins/controlValue';
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';
import Tooltip from '@/components/phila-ui/Tooltip';
import { Textbox, Dropdown } from '@phila/phila-ui';
import { withValidation } from 'vee-validate';

const TextboxVee = withValidation(Textbox);
const DropdownVee = withValidation(Dropdown);

Vue.use(ClientTable, {}, false, 'bulma');

export default {
  name: 'Creditors',
  components: {
    CheckboxAdv,
    TextboxVee,
    Textbox,
    Dropdown,
    DropdownVee,
    Tooltip,
  },
  mixins: [
    filerScreenHelpers,
    controlValueHelpers,
  ],
  data() {
    return {
      currentFiling: {},
      items: [],
      isSaving: false,
      newItem: {
        name: '',
        interestRate: '',
        loanAmount: '',
        streetAddress: '',
        city: '',
        state: '',
        zip: '',
      },
      nothingToReport: false,
      tableOptions: {
        columnsClasses: {
          'edit': 'actions has-text-centered',
          'delete': 'actions has-text-centered',
          'clone': 'actions has-text-centered',
        },
        pagination: {
          dropdown: false,
          edge: true,
        },
        sortable: [],
        perPage: 50,
        perPageValues: [],
        filterable: false,
        headings: {
          name: 'Creditor Name',
          interestRate: 'Rate %',
          streetAddress: 'Address',
        },
      },
      table: [
        'name',
        'interestRate',
        'streetAddress',
        'edit',
        'clone',
        'delete',
      ],
    };
  },
  computed: {
    loanAmounts () {
      return this.controlValueDropdown('loanamount');
    },
    states () {
      return this.controlValueDropdown('state');
    },
    creditorsText () {
      return this.$store.getters[ 'admin/activeControlValues' ]('creditorsText');
    },
    creditorsTooltip () {
      return this.$store.getters[ 'admin/activeControlValues' ]('creditorsTooltip');
    },
    creditorsTooltipText () {
      let tooltip = [];
      if (this.currentFiling && this.creditorsTooltip) {
        if (this.currentFiling.stateForm === 'Y') {
          tooltip.push(this.creditorsTooltip[0].description);
        }
        if (this.currentFiling.cityForm === 'Y') {
          tooltip.push(this.creditorsTooltip[1].description);
        }
      }
      return tooltip.join('<br>');
    },
  },
  watch: {
    items: {
      deep: true,
      handler: function (items) {
        items.forEach(item => {
          if (item.interestRate) {
            item.interestRate = parseFloat(item.interestRate);
          }
        });
      },
    },
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    this.$store.commit('loading', false);
  },
  methods: {
    async initialAssign () {
      this.currentFiling = this.getCurrentFiling();
      this.items = this.getCurrentCreditors();
      this.hasNothingToReport();
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'creditorsText'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'creditorsTooltip'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'state'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'loanamount'));
      promises.push(this.$store.dispatch('creditors/fetchCreditors'));
      return Promise.all(promises);
    },
    hasNothingToReport () {
      if (this.items.length === 0 && this.currentFiling.creditorsCompleted === 'Y') {
        this.nothingToReport = true;
      }
    },
    getCurrentCreditors () {
      return this.cloneObj(this.$store.state.creditors.creditors);
    },
    saveAndContinue () {
      let promises = [];

      const col = 'creditorsCompleted';
      const endpoint = 'creditors';

      let filing = {
        id: this.currentFiling.id,
      };

      //V-model.number won't work here, so we need to parse it ourselves
      this.items.forEach(item => {
        item.interestRate = parseFloat(item.interestRate);
      });

      filing[col] = this.currentFiling[col];

      let isDeletingAll = this.isDeletingAllItems();

      if (this.nothingToReport) {
        filing[col] = 'Y';
      } else {
        if (this.items.length === 0) {
          filing[col] = 'N';
        } else {
          promises.push(this.$store.dispatch('filing/saveFilingScreen', {
            items: this.items,
            endpoint,
          }));
          if (isDeletingAll === true) {
            filing[col] = 'N';
          } else {
            filing[col] = 'Y';
          }

        }
      }

      let self = this;

      promises.push(this.$store.dispatch('filing/saveFiling', filing).then(() => {
        if (filing[col] === 'Y' && this.nothingToReport === true) {

          self.items.forEach(item => {
            self.deleteItem(item.id);
          }, self);

          self.$store.dispatch('filing/saveFilingScreen', {
            items: self.items,
            endpoint,
          });

        }
      }));


      Promise.all(promises).then(() => {
        this.$router.push({ name: this.nextFilingScreen });
      });
    },
    async trigerNewItemAction () {
      const itemRef = this.$refs['form-creditors'];
      const isValid = await itemRef.validate();

      if (!isValid) {
        return false;
      }

      if (this.tableAction === 'Add') {
        this.addNewItem();
      }
      if (this.tableAction === 'Update') {
        this.updateItem();
      }
      itemRef.reset();
    },
  },
};
</script>
